import { format, differenceInDays, startOfWeek, addWeeks, addDays, isSameMonth } from 'date-fns';
import { TranslationKeyEnum } from '../types/constants';
import { TagVariant } from '@hills-design-system/components';
import { toZonedTime } from 'date-fns-tz';
import { ReportStatus } from '../types/reports';
import { convertUTCToLocalDate } from './dateUtils';

/**
 * Formats a due date string for display in report status tags
 * @param dueDate - ISO date string or null/undefined
 * @param t - Translation function
 * @returns Formatted due date label (e.g., "Due Sep. 7" or "Due soon")
 */
export const formatDueDateLabel = (
  dueDate: string | null | undefined,
  t: (key: TranslationKeyEnum, ...args: string[]) => string
): string => {
  if (!dueDate) {
    return t(TranslationKeyEnum.DueSoon);
  }

  try {
    const formattedDate = format(toZonedTime(dueDate, 'UTC'), 'MMM. d');
    return t(TranslationKeyEnum.DueOn, formattedDate);
  } catch {
    return `${t(TranslationKeyEnum.DueOn)} ${dueDate}`;
  }
};

/**
 * Determines the appropriate tag variant based on how many days until the due date
 * @param status - status of the report
 * @param isWarning - whether the report is a warning
 * @returns TagVariant.Blue if due but no warning, TagVariant.Yellow if due and warning
 */
export const getDueDateTagVariant = (
  status: string,
  isWarning: boolean | undefined
): TagVariant => {
  if (!status) {
    return TagVariant.Blue;
  }

  if ((status === 'due' || status === ReportStatus.Pending) && isWarning) {
    return TagVariant.Yellow;
  }

  if ((status === 'due' || status === ReportStatus.Pending) && !isWarning) {
    return TagVariant.Blue;
  }

  // Default return value if no conditions are met
  return TagVariant.Blue;
};

/**
 * Gets the highest priority report from an array of reports with 'due' or 'late' status
 * Priority: Reports < 3 days from due date first, then by closest due date
 * @param reports - Array of reports with dueDate property
 * @returns The highest priority report or undefined if no pending reports
 */
export const getHighestPriorityPendingReport = <
  T extends { dueDate?: string | null; status: string },
>(
  reports: T[]
): T | undefined => {
  const pendingReports = reports.filter((report) => report.status === 'due');

  if (pendingReports.length === 0) {
    return undefined;
  }

  if (pendingReports.length === 1) {
    return pendingReports[0];
  }

  // Sort by priority: reports within 3 days first, then by closest due date
  return pendingReports.slice().sort((a, b) => {
    if (!a.dueDate || !b.dueDate) {
      return 0;
    }

    try {
      const today = new Date();
      const dueDateA = toZonedTime(a.dueDate, 'UTC');
      const dueDateB = toZonedTime(b.dueDate, 'UTC');
      const daysUntilDueA = differenceInDays(dueDateA, today);
      const daysUntilDueB = differenceInDays(dueDateB, today);

      // Priority 1: Reports within 3 days take precedence
      const isUrgentA = daysUntilDueA <= 3;
      const isUrgentB = daysUntilDueB <= 3;

      if (isUrgentA && !isUrgentB) return -1;
      if (!isUrgentA && isUrgentB) return 1;

      // Priority 2: If both urgent or both not urgent, sort by closest due date
      return daysUntilDueA - daysUntilDueB;
    } catch {
      return 0;
    }
  })[0];
};

/**
 * Calculates the start and end dates of the next-to-next weekly reporting window.
 * If no join date is provided, it uses the current date.
 *
 * @param {string | undefined} joinProgramDate The ISO string of the user's join date.
 * @returns {string} The formatted date range string, e.g., "Jan. 15-21" or "Nov. 28 - Dec. 4".
 */
export const calculatePetParentReportingWindow = (joinProgramDate: string | undefined) => {
  // Use the provided join date, or fall back to the current date if not provided.
  const dateToProcess = joinProgramDate
    ? (utcCalendarDateToLocal(joinProgramDate) ?? new Date())
    : new Date();

  // Find the start of the week for the date to process.
  // By default, startOfWeek starts on Sunday.
  const startOfThisWeek = startOfWeek(dateToProcess);

  // Add two full weeks to get to the next-to-next week.
  const startOfNextToNextWeek = addWeeks(startOfThisWeek, 2);

  // The Monday of the target week is one day after the start of the next-to-next week.
  const mondayDate = addDays(startOfNextToNextWeek, 1);
  const formattedMonday = format(mondayDate, 'MMM. d');

  // The Sunday of the target week is six days after the Monday.
  const sundayDate = addDays(mondayDate, 6);

  // Now we check if the Monday and Sunday dates are in the same month.
  const datesAreInSameMonth = isSameMonth(mondayDate, sundayDate);

  // Format the dates based on the result of the check.
  if (datesAreInSameMonth) {
    // If same month, only show the day for the second date.
    const formattedSunday = format(sundayDate, 'd');
    return `${formattedMonday}-${formattedSunday}`;
  } else {
    // If different months, show the month and day for both.
    const formattedSundayWithMonth = format(sundayDate, 'MMM. d');
    return `${formattedMonday} - ${formattedSundayWithMonth}`;
  }
};

/**
 * Calculate the Monday date of the reporting window that opens two weeks from now.
 * Used to display the specific date when the report opens.
 *
 * @param {string | undefined} joinProgramDate The ISO string of the user's join date.
 * @returns {string} The formatted Monday date, e.g., "Sep. 14".
 */
export const getPetParentReportMondayDate = (joinProgramDate: string | undefined) => {
  // Use the provided join date, or fall back to the current date if not provided.
  const dateToProcess = joinProgramDate
    ? (utcCalendarDateToLocal(joinProgramDate) ?? new Date())
    : new Date();

  // Find the start of the week for the date to process.
  const startOfThisWeek = startOfWeek(dateToProcess);

  // Add two full weeks to get to the next-to-next week.
  const startOfNextToNextWeek = addWeeks(startOfThisWeek, 2);

  // The Monday of the target week is one day after the start of the next-to-next week.
  const mondayDate = addDays(startOfNextToNextWeek, 1);

  // Format as "MMM. d" (e.g., "Sep. 14")
  return format(mondayDate, 'MMM. d');
};

/**
 * Converts a UTC calendar date to a local Date object with the same calendar date.
 * Use this for dates that represent calendar dates (not timestamps).
 *
 * This is needed because dates stored as UTC midnight (e.g., "2025-01-13T00:00:00.000Z")
 * will display as the previous day in negative UTC offset timezones when converted to local time.
 * For example, Jan 13 00:00 UTC becomes Jan 12 16:00 PST in Los Angeles (UTC-8).
 *
 * @param utcDateOrString - Date object or ISO string representing UTC midnight
 * @returns Local Date object with the same calendar date
 *
 * @example
 * // For a user in Los Angeles (UTC-8):
 * const utcDate = "2025-01-13T00:00:00.000Z";
 * const localDate = utcCalendarDateToLocal(utcDate);
 * // localDate represents Jan 13, 2025 at midnight in local time
 * // (not Jan 12, 2025 at 4:00 PM which is what new Date(utcDate) would give)
 */
export function utcCalendarDateToLocal(
  utcDateOrString: Date | string | null | undefined
): Date | null {
  return convertUTCToLocalDate(utcDateOrString);
}

/**
 * Format the Mon–Sun window string from a known Monday ISO date.
 * Use when the Monday is already known (e.g. from backend firstExpectedReport).
 */
export function formatPetParentWindowFromTimeframe(timeframeStartISO: string): string {
  const mondayDate = utcCalendarDateToLocal(timeframeStartISO);
  if (!mondayDate) return calculatePetParentReportingWindow(timeframeStartISO);
  const sundayDate = addDays(mondayDate, 6);
  if (isSameMonth(mondayDate, sundayDate)) {
    return `${format(mondayDate, 'MMM. d')}-${format(sundayDate, 'd')}`;
  }
  return `${format(mondayDate, 'MMM. d')} - ${format(sundayDate, 'MMM. d')}`;
}

/**
 * Format the Monday label from a known Monday ISO date.
 * Use when the Monday is already known (e.g. from backend firstExpectedReport).
 */
export function formatPetParentMondayFromTimeframe(timeframeStartISO: string): string {
  const mondayDate = utcCalendarDateToLocal(timeframeStartISO);
  if (!mondayDate) return getPetParentReportMondayDate(timeframeStartISO);
  return format(mondayDate, 'MMM. d');
}
